"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.WsClientSync = exports.WsDataBucketEvent = exports.WsClientSyncEvent = void 0;
const eventemitter3_1 = __importDefault(require("eventemitter3"));
const invert_1 = __importDefault(require("lodash/invert"));
const merge_1 = __importDefault(require("lodash/merge"));
const constants_iso_1 = require("@wranggle/constants-iso");
const shared_utils_iso_1 = require("@wranggle/shared-utils-iso");
const ws_client_1 = require("@wranggle/ws-client");
const interfaces_1 = require("./interfaces");
Object.defineProperty(exports, "WsClientSyncEvent", { enumerable: true, get: function () { return interfaces_1.WsClientSyncEvent; } });
Object.defineProperty(exports, "WsDataBucketEvent", { enumerable: true, get: function () { return interfaces_1.WsDataBucketEvent; } });
const ws_data_bucket_1 = require("./internal/ws-data-bucket");
class WsClientSync extends eventemitter3_1.default {
    constructor(args) {
        super();
        this._connState = ws_client_1.WsConnectionState.Disconnected;
        this._wsDataBucketByTopic = {};
        this._wsTopicByAuthorVarName = {}; // Record<AuthorVarName, WsTopic>
        this._topicsToIgnore = new Set();
        this.__bucketListenerByTopicByBucketEvent = {};
        this._fetchConnectionInstructions = args.fetchConnectionInstructions;
        this._browserWindowUid = shared_utils_iso_1.createUid();
        this._browserSessionUid = this._tryBrowserSessionUid();
        this._initWsReconnectingClient(args);
        this._logger = args.logger || shared_utils_iso_1.loggerIsoLib();
    }
    leaveTopic(wsTopic) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!wsTopic) {
                throw new shared_utils_iso_1.PlusPlusPlus('TopicRequired');
            }
            if (wsTopic.startsWith(interfaces_1.WsClientSyncControlChannel)) {
                throw new shared_utils_iso_1.PlusPlusPlus('CannotLeaveWiredTopic', { wsTopic });
            }
            this._topicsToIgnore.add(wsTopic);
            const bucket = this._bucket(wsTopic);
            if (!bucket) {
                return;
            }
            delete this._wsDataBucketByTopic[wsTopic];
            bucket.stop();
            if (bucket.getIsSubscribed()) {
                this._wsClient && this._wsClient.leaveTopics(wsTopic);
            }
            this._removeBucketEventRelay(wsTopic); // prob not necessary, since bucket should not emit once stopped
        });
    }
    stop() {
        Object.keys(this._wsDataBucketByTopic).forEach((wsTopic) => this.leaveTopic(wsTopic));
    }
    getWsConnectionState() {
        return this._connState;
    }
    getDataBucketByAuthorVarName(authorVarName) {
        const wsTopic = this._wsTopicByAuthorVarName[authorVarName];
        return this._wsDataBucketByTopic[wsTopic];
    }
    getAllDataBucketsByAuthorVarName() {
        const res = {};
        Object.keys(this._wsTopicByAuthorVarName).forEach((authorVarName) => {
            const bucket = this.getDataBucketByAuthorVarName(authorVarName);
            if (bucket) {
                res[authorVarName] = bucket;
            }
        });
        return res;
    }
    getAllDataBuckets() {
        return Object.values(this._wsDataBucketByTopic);
    }
    getStateByVarName() {
        const res = {};
        const bucketsByName = this.getAllDataBucketsByAuthorVarName();
        Object.keys(bucketsByName).forEach(varName => {
            res[varName] = bucketsByName[varName].getState();
        });
        return res;
    }
    getBrowserWindowUid() {
        return this._browserWindowUid;
    }
    getBrowserSessionUid() {
        return this._browserSessionUid;
    }
    _bucket(wsTopic) {
        return this._wsDataBucketByTopic[wsTopic];
    }
    _ensureBucket(wsTopic) {
        if (!this._bucket(wsTopic)) {
            const bucket = this._wsDataBucketByTopic[wsTopic] = new ws_data_bucket_1.WsDataBucket({
                wsTopic,
            });
            this._setupBucketEventRelay(bucket);
        }
        return this._wsDataBucketByTopic[wsTopic];
    }
    _initWsReconnectingClient(args) {
        const reconnectionOpts = merge_1.default({ isReconnectDesired: args.isReconnectDesired }, args.reconnectionOpts);
        this.__messageEventListener = this._handleIncomingMessage.bind(this);
        this.__connectionStateListener = this._handleWsConnectionStateChange.bind(this);
        const _connectionInstructionsAndUrl = () => __awaiter(this, void 0, void 0, function* () {
            const { wsUrl, afterConnectCallbackUrl } = yield this._fetchConnectionInstructions({
                browserWindowUid: this.getBrowserWindowUid(),
                browserSessionUid: this.getBrowserSessionUid(),
            });
            if (!wsUrl || !afterConnectCallbackUrl) {
                throw new shared_utils_iso_1.PlusPlusPlus('FailedToFetchWsTopicConnectionInstructions', {
                // reason: 'Call __ when ready to continue.',  // need to first expose this._wsClient.reconnect fn
                });
            }
            this._afterConnectCallbackUrl = afterConnectCallbackUrl;
            return wsUrl;
        });
        this._wsClient = new ws_client_1.WsReconnectingClient(Object.assign(Object.assign({}, reconnectionOpts), { requestAuthenticatedWsUrl: _connectionInstructionsAndUrl }));
        this._wsClient.addListener('message', this.__messageEventListener);
        this._wsClient.connStateObserver().on(ws_client_1.ConnectionStateChangeEvent, this.__connectionStateListener);
    }
    _handleWsConnectionStateChange(connState) {
        if (connState === this._connState) {
            return;
        }
        this._connState = connState;
        if (connState === ws_client_1.WsConnectionState.Disconnected) {
            Object.keys(this._wsDataBucketByTopic).forEach(wsTopic => {
                const bucket = this._bucket(wsTopic);
                bucket && bucket.markDisconnected();
            });
        }
        if (connState === ws_client_1.WsConnectionState.Connected) {
            this._askServerForFullDataSync()
                .catch(err => {
                console.warn('Error encountered while trying to restore state after websocket reconnected', err);
            });
        }
        this.emit(ws_client_1.ConnectionStateChangeEvent, connState);
    }
    _askServerForFullDataSync() {
        return __awaiter(this, void 0, void 0, function* () {
            yield shared_utils_iso_1.httpFetchWithRetriesAndTimeout({
                url: this._afterConnectCallbackUrl,
                method: 'GET',
                requestTimeoutMs: 15000,
                retryOptions: {
                    retries: 5,
                    minTimeout: 1000,
                    maxTimeout: 5000,
                },
            });
            // todo: use this._afterConnectCallbackUrl; w/ retries?
        });
    }
    _handleIncomingMessage(evtOrRawMsg) {
        let data;
        try {
            data = ws_client_1.extractAndParseMessage(evtOrRawMsg);
        }
        catch (err) {
            console.warn('Ignoring invalid websocket message', err, evtOrRawMsg);
            return;
        }
        if (data.wsTopic && data.wsTopic.startsWith(interfaces_1.WsClientSyncControlChannel)) {
            this._initializeStateCommand(data);
        }
        else if (ws_client_1.isHousekeepingMessage(data)) {
            this._serverSendingTopicJoinStatus(data.topicsJoined);
        }
        else if (data.clientReducerActions && data.wsTopic) {
            this._handleSyncCommandFromServer(data);
        }
        else {
            this.logger().debug({ data }, 'Invalid message format');
            this.logger().info('Ignoring websocket message, not a sync or control command');
        }
    }
    logger() {
        return this._logger;
    }
    _initializeStateCommand(command) {
        const { wsTopicByVarName, clientReducerActions, responseTarget } = command;
        if (!wsTopicByVarName || !Array.isArray(clientReducerActions)) {
            throw new shared_utils_iso_1.PlusPlusPlus('InvalidWsClientSyncControlMessage', {
                reason: 'wsTopicByVarName and clientReducerActions required',
                payload: command,
            });
        }
        Object.assign(this._wsTopicByAuthorVarName, wsTopicByVarName);
        const varNameByTopic = invert_1.default(this._wsTopicByAuthorVarName);
        clientReducerActions.forEach((action) => {
            const { wsTopic } = action.actionMeta || {};
            if (!wsTopic) {
                throw new shared_utils_iso_1.PlusPlusPlus('InvalidWsClientSyncControlMessage', {
                    reason: 'actionMeta.wsTopic required on ClientReducerAction when initializing dataBucket',
                    action,
                });
            }
            const bucket = this._ensureBucket(wsTopic);
            bucket.setAuthorVarName(varNameByTopic[wsTopic]);
            bucket._serverTopicSubscriptionResponse(true);
            bucket.initialDataReceived(action);
        });
        this._resolveResponseTarget(responseTarget, command);
    }
    _handleSyncCommandFromServer(command) {
        const { clientReducerActions, wsTopic, responseTarget } = command;
        if (this._topicsToIgnore.has(wsTopic)) {
            return; // handle responseTarget?
        }
        const bucket = this._bucket(wsTopic);
        if (!bucket) {
            console.log('Ignoring ClientReducerAction for unknown topic', wsTopic);
            return;
        }
        clientReducerActions.forEach((action) => {
            bucket._processMessage(action);
        });
        this._resolveResponseTarget(responseTarget, command);
    }
    _serverSendingTopicJoinStatus(topicsJoined) {
        if (topicsJoined && typeof topicsJoined === 'object') {
            Object.keys(topicsJoined).forEach((wsTopic) => {
                const isJoined = topicsJoined[wsTopic];
                if (isJoined && !this._topicsToIgnore.has(wsTopic)) {
                    const parts = wsTopic.split('/');
                    if (parts[parts.length - 1] === 'scopedDef') { // placeholder.. todo: to constants, and this approach seems meh, needs more thought
                        this._ensureBucket(wsTopic);
                    }
                }
            });
        }
    }
    /**
     * Sets up event realying, so an event on any bucket is emitted on the WsClientSync instance as well
     * @param bucket
     */
    _setupBucketEventRelay(bucket) {
        const wsTopic = bucket.getWsTopic();
        this.__bucketListenerByTopicByBucketEvent[wsTopic] = {};
        // weird, didn't eventemitter3 support wildcard event names?
        allRelayedWsDataBucketEvents().forEach(eventName => {
            const relay = (...args) => {
                this.emit(eventName, bucket, ...args);
            };
            this.__bucketListenerByTopicByBucketEvent[wsTopic][eventName] = relay;
            bucket.on(eventName, relay);
        });
    }
    _removeBucketEventRelay(wsTopic) {
        const listeners = this.__bucketListenerByTopicByBucketEvent[wsTopic];
        const bucket = this._bucket(wsTopic);
        if (listeners && bucket) {
            allRelayedWsDataBucketEvents().forEach(eventName => {
                bucket.off(eventName, listeners[eventName]);
            });
        }
    }
    _resolveResponseTarget(responseTarget, command) {
        if (!responseTarget || !responseTarget.requestUid) {
            return;
        }
        const requestUid = responseTarget.requestUid;
        requestUid && this.emit(interfaces_1.WsClientSyncEvent.ResponseTargetReceived, requestUid, command);
    }
    _tryBrowserSessionUid() {
        if (!global['sessionStorage']) {
            return;
        }
        let res = sessionStorage[constants_iso_1.SessionStorageVarName.BrowserSessionUid];
        if (!res) {
            res = sessionStorage[constants_iso_1.SessionStorageVarName.BrowserSessionUid] = shared_utils_iso_1.createUid();
        }
        return res;
    }
}
exports.WsClientSync = WsClientSync;
const RelayedWsDataBucketEvents = [
    interfaces_1.WsDataBucketEvent.JoinStatusChange,
    interfaces_1.WsDataBucketEvent.StateChange,
    interfaces_1.WsDataBucketEvent.DataBucketSyncAction,
    interfaces_1.WsDataBucketEvent.DataBucketReady,
];
function allRelayedWsDataBucketEvents() {
    return RelayedWsDataBucketEvents;
}
//# sourceMappingURL=ws-client-sync.js.map